import React from 'react';
import { Alert, Container, Row, Col, Card, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Registration, { RegistrationTypes } from '../Registration/Registration';
import RegionSummary from '../../components/RegionSummary/RegionSummary';
import PaymentSelectButton from '../../components/PaymentSelectButton/PaymentSelectButton';
import CurrencyText from '../../components/CurrencyText/CurrencyText';
import CreditCardForm from '../../components/CreditCardForm/CreditCardForm';
import Contact from '../../components/Contact/Contact';
import Success from '../Success/Success';
import WhatsNextPopover from '../../components/WhatsNextPopover/WhatsNextPopover';
import CalculatorLayout from '../../components/CalculatorLayout/CalculatorLayout';

import { isPaymentRegion } from '../../utils/helpers';
import {
  getRegionFromForm,
  isPledgeOnlyRegion,
  getRateFromForm,
  calculateHomeownerPayment
} from '../../utils/paymentHelpers';
import { handlePaymentSubmission } from '../../utils/paymentSubmission';

import styles from './ImAHomeowner.module.css';

const today = moment();
const currentYear = today.year();

class ImAHomeowner extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      mode: "register",
      registration: null,
      region: null,
      term: "monthly",
      monthlyPayment: 0,
      yearlyPayment: 0,
      address: '',
      payForPrevious: false,
      previousYears: 2,
      publicMap: true,
      subscribeAnnually: true,
      total: 0,
      currency: 'cad',
      description: 'Monthly reciprocity contribution',
      pledgeOnly: false,
      years: [],
      error: '',
      creditCardPayment: null
    };
  }

  calculatePayments = async form => {
    try {
      const region = await getRegionFromForm(form);
      const rate = getRateFromForm(form);
      const monthlyPayment = Math.ceil(calculateHomeownerPayment(form.value, rate, region.id));
      const yearlyPayment = Math.ceil(calculateHomeownerPayment(form.value, rate, region.id) * 12);

      this.setState({
        mode: "calculate",
        registration: form,
        region,
        monthlyPayment,
        yearlyPayment,
        address: form.address,
        total: monthlyPayment,
        pledgeOnly: isPledgeOnlyRegion(region)
      });
    } catch (error) {
      this.setState({ mode: 'error', error: error.message });
    }
  }

  setActivePaymentType = type => {
    this.setState({
      term: type
    }, () => {
      this.calculateTotals();
    });
  }

  handlePayForPreviousChange = () => {
    this.setState({ payForPrevious: !this.state.payForPrevious }, () => {
      this.calculateTotals();
    });
  }

  handleSubscribeAnnuallyChange = () => {
    this.setState({ subscribeAnnually: !this.state.subscribeAnnually }, () => {
      this.calculateTotals();
    });
  }

  handlePreviousYearsChange = event => {
    this.setState({ previousYears: parseInt(event.target.value) }, () => {
      this.calculateTotals();
    });
  }

  calculateTotals = () => {
    let total = 0;
    const { term, monthlyPayment, yearlyPayment, payForPrevious, previousYears } = this.state;
    if (term === 'monthly') {
      total = monthlyPayment;
    } else {
      total = yearlyPayment;
      if (payForPrevious) {
        total += (yearlyPayment * previousYears);
      }
    }
    this.setState({ total });
  }

  handlePublicMapChange = () => {
    this.setState({ publicMap: !this.state.publicMap });
  }

  handleCreditCardSubmit = async creditCardPayment => {
    this.setState({ creditCardPayment }, async () => {
      await handlePaymentSubmission({
        state: this.state,
        creditCardPayment,
        onSuccess: () => this.setState({ mode: "success" }),
        onError: (error) => this.setState({ mode: "error", error }),
        pageType: 'homeowner'
      });
    });
  }

  render() {
    const { mode, region, term, monthlyPayment, yearlyPayment, address, payForPrevious, previousYears, publicMap, subscribeAnnually, total } = { ...this.state };

    return (
      <CalculatorLayout title="'m a homeowner" highlightChar="i" hideTitle={mode === 'success'}>
        {mode === 'register' &&
          <Registration type={RegistrationTypes.homeowner} image={'/assets/reciprocity-homeowner-logo.png'} submitText="Calculate my reciprocity payment"
            buttonColor="#80C0E9"
            subText={
              <>
                <p>
                  Reciprocity payments offer a simple, accessible way for homes, businesses, and
                  events to act on the Indigenous principles that shaped and were shaped by the lands and
                  waters we love and rely on. They are voluntary, ongoing contributions that move beyond
                  symbolic acknowledgment toward meaningful action and meaningful relationships.
                </p>
                <p>
                  Type in your home address and we'll calculate your recommended Reciprocity Payment. It's about 1% of your property taxes per month (12% annually). Find out what reciprocity looks like for your property.
                </p>
              </>
            }
            onSuccess={this.calculatePayments}
          />
        }
        {mode === 'success' &&
          <Success pledgeOnly={this.state.pledgeOnly} type='homeowner' years={this.state.years} />
        }
        {mode === 'error' &&
          <Alert variant='danger'>{this.state.error}</Alert>
        }
        {mode === 'calculate' &&
          <Container className='mb-5'>
            <Row>
              <RegionSummary region={region ? region : { key: 'rtf' }} />
            </Row>
            <Row>
              {isPaymentRegion(region.id) ?
                <div className='mb-3'>
                  <p style={{ fontWeight: 'bold' }}>
                    We've calculated your recommended Reciprocity Payment for {currentYear} at a rate of 1% of your annual property taxes per month (12% of annual property tax). Select your preferred method of payment below.
                  </p>
                  <p style={{ fontWeight: 'bold' }}>
                    If you'd like to pay a different amount, reach out to us at <a href="mailto:hello@reciprocityconnects.ca" target="_blank" rel="noreferrer" style={{ fontWeight: 'bold' }}>hello@reciprocityconnects.ca</a>
                  </p>
                </div>
                :
                <p className='mb-3' style={{ fontWeight: 'bold' }}>
                  We've calculated your recommended Reciprocity Payment but we're not set up to take payments in your area yet. Sign up below and we'll get in touch once we're ready to go.
                </p>
              }
            </Row>
            <Row className={styles.buttonRow} xs={1} sm={2}>
              <Col className={`mb-4 mb-sm-0`}>
                <PaymentSelectButton
                  isSelected={term === "yearly"}
                  type="yearly"
                  address={address}
                  paymentValue={yearlyPayment}
                  year={currentYear}
                  setActivePaymentType={this.setActivePaymentType} />
              </Col>
              <Col>
                <PaymentSelectButton
                  isSelected={term === "monthly"}
                  type="monthly"
                  address={address}
                  paymentValue={monthlyPayment}
                  year={currentYear}
                  setActivePaymentType={this.setActivePaymentType} />
              </Col>
            </Row>
            <Row xs={1} md={2}>
              {term === 'yearly' &&
                <Col>
                  <Card className={styles.wantMoreCard}>
                    <Card.Header>Want more?</Card.Header>
                    <Card.Body>
                      <Form>
                        <Row>
                          <Col xs={8}>
                            <Form.Check type='checkbox' id='payForPrevious' checked={payForPrevious} label="I'd like to pay for previous years"
                              onChange={this.handlePayForPreviousChange}
                            />
                          </Col>
                          <Col xs={2}>
                            <Form.Control type='number' min='1' value={previousYears} disabled={!payForPrevious}
                              onChange={this.handlePreviousYearsChange}
                            />
                          </Col>
                          <Col xs={2} className='text-right'>
                            years
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={9}>
                            <Form.Check type='checkbox' id='subscribeAnnually' checked={subscribeAnnually} label='Subscribe to an ongoing annual contribution'
                              onChange={this.handleSubscribeAnnuallyChange}
                            />
                          </Col>
                          <Col xs={3} className='text-right'>
                            <CurrencyText value={yearlyPayment} />/year
                          </Col>
                        </Row>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
              }
              {term === 'monthly' &&
                <Col />
              }
              <Col>
                <Card className={styles.totalCard}>
                  <Card.Header>Total Payments Today:</Card.Header>
                  <Card.Body>
                    {term === 'yearly' &&
                      <Row>
                        <Col xs={8}>
                          Subscription Payment<br />
                          Billed annually
                        </Col>
                        <Col xs={4} className='text-right'>
                          <CurrencyText value={yearlyPayment} />
                        </Col>
                      </Row>
                    }
                    {term === 'yearly' && payForPrevious &&
                      <Row>
                        <Col xs={8}>
                          Payment for Previous Years
                        </Col>
                        <Col xs={4} className='text-right'>
                          <CurrencyText value={previousYears * yearlyPayment} />
                        </Col>
                      </Row>
                    }
                    {term === 'monthly' &&
                      <Row>
                        <Col xs={8}>
                          Subscription Payment<br />
                          Starting today, {today.format('MMM. D, yyyy')}<br />
                          Billed each month on the {today.format('Do')}
                        </Col>
                        <Col xs={4} className='text-right'>
                          <CurrencyText value={monthlyPayment} />/month
                        </Col>
                      </Row>
                    }
                    <hr className={`${styles.hr} my-1`} />
                    <Row className={styles.totalRow}>
                      <Col xs={8}>
                        Total
                      </Col>
                      <Col xs={4} className='text-right'>
                        <CurrencyText value={total} />
                      </Col>
                    </Row>
                    {term === 'yearly' && subscribeAnnually &&
                      <Row className={styles.subscriptionRow}>
                        <Col xs={8}>
                          Subscription Payment<br />
                          Billed annually on {today.format('MMM Do')}
                        </Col>
                        <Col xs={4} className='text-right'>
                          <CurrencyText value={yearlyPayment} />/year
                        </Col>
                      </Row>
                    }
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col xs={0} md={2} lg={3} />
              <Col xs={12} md={8} lg={6}>
                <Form.Check className={styles.publicMap} type='checkbox' id='publicMap' checked={publicMap}
                  label={
                    <div>
                      <span>I'd like to be recognized on the </span>
                      <Link to={'/reciprocity-map'} target='_blank'>Reciprocity Map.</Link>
                      <span> Please note: your name or address will never be shown on the map.</span>
                    </div>
                  }
                  onChange={this.handlePublicMapChange}
                />
                <div className='d-flex justify-content-center'>
                  <WhatsNextPopover />
                </div>
                <CreditCardForm className={styles.creditCardForm} order={this.state} submit={this.handleCreditCardSubmit} />
                <div className='text-justify mt-4 mb-3'>
                  <small><i>Reciprocity respects your privacy. By clicking {this.state.pledgeOnly ? "'PLEDGE NOW'" : "'MAKE MY RECIPROCITY PAYMENT'"} you consent to share your information with Reciprocity and receive updates, including when we are up and running in your area, by email, text and/or phone. You can unsubscribe at any time.</i></small>
                </div>
                <Contact type='problem' className='text-center pt-2' />
              </Col>
              <Col xs={0} md={2} lg={3} />
            </Row>
          </Container>
        }
      </CalculatorLayout>
    );
  };
}

export default ImAHomeowner;
